import React, { useState, useEffect } from 'react';
import { getAdminToken } from '../services/authService';

const CompatibilityAdmin = () => {
  const [compatibilityData, setCompatibilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(25);
  const [saveStatus, setSaveStatus] = useState('');

  // Form state for adding/editing
  const [formData, setFormData] = useState({
    connector_a: '',
    connector_b: '',
    compatible: true,
    connection_type: 'balanced',
    poles_used: '',
    warning: '',
    notes: '',
    reason: ''
  });

  useEffect(() => {
    loadCompatibilityData();
  }, []);

  const loadCompatibilityData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/config-manager?type=compatibility', {
        headers: {
          'Authorization': getAdminToken(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load compatibility data');
      }

      const data = await response.json();
      setCompatibilityData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load compatibility data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (entry) => {
    try {
      setSaveStatus('saving');
      const response = await fetch('/api/config-manager', {
        method: 'PUT',
        headers: {
          'Authorization': getAdminToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'compatibility',
          data: entry
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save compatibility entry');
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
      loadCompatibilityData();
      setEditingEntry(null);
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to save: ' + err.message);
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this compatibility entry?')) {
      return;
    }

    try {
      const response = await fetch(`/api/config-manager?type=compatibility&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAdminToken()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete compatibility entry');
      }

      loadCompatibilityData();
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      connector_a: '',
      connector_b: '',
      compatible: true,
      connection_type: 'balanced',
      poles_used: '',
      warning: '',
      notes: '',
      reason: ''
    });
  };

  const handleEdit = (entry) => {
    setFormData({
      id: entry.id,
      connector_a: entry.connector_a,
      connector_b: entry.connector_b,
      compatible: entry.compatible,
      connection_type: entry.connection_type || 'balanced',
      poles_used: entry.poles_used || '',
      warning: entry.warning || '',
      notes: entry.notes || '',
      reason: entry.reason || ''
    });
    setEditingEntry(entry.id);
    setShowAddForm(false);
  };

  const handleAdd = () => {
    resetForm();
    setEditingEntry(null);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setEditingEntry(null);
    setShowAddForm(false);
    resetForm();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.connector_a || !formData.connector_b) {
      setError('Both connectors are required');
      return;
    }

    handleSave(formData);
  };

  // Filter and search
  const filteredData = compatibilityData.filter(entry => {
    const matchesSearch = 
      entry.connector_a.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.connector_b.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.notes && entry.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.warning && entry.warning.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = 
      filterType === 'all' || 
      (filterType === 'compatible' && entry.compatible) ||
      (filterType === 'incompatible' && !entry.compatible);

    return matchesSearch && matchesFilter;
  });

  // Pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  if (loading) {
    return (
      <div className="compatibility-admin">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading compatibility matrix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="compatibility-admin">
      <div className="admin-header">
        <h2>🔌 Compatibility Matrix Management</h2>
        <p>Manage connector compatibility rules and relationships</p>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {saveStatus && (
        <div className={`save-status ${saveStatus}`}>
          {saveStatus === 'saving' && '💾 Saving...'}
          {saveStatus === 'saved' && '✅ Saved successfully!'}
          {saveStatus === 'error' && '❌ Save failed!'}
        </div>
      )}

      {/* Controls */}
      <div className="admin-controls">
        <div className="control-group">
          <input
            type="text"
            placeholder="Search connectors, notes, warnings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Entries</option>
            <option value="compatible">Compatible Only</option>
            <option value="incompatible">Incompatible Only</option>
          </select>
          
          <button onClick={handleAdd} className="btn btn-primary">
            + Add New Compatibility Rule
          </button>
        </div>

        <div className="stats">
          <span>Total Entries: {compatibilityData.length}</span>
          <span>Filtered: {filteredData.length}</span>
          <span>Compatible: {compatibilityData.filter(e => e.compatible).length}</span>
          <span>Incompatible: {compatibilityData.filter(e => !e.compatible).length}</span>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingEntry) && (
        <div className="edit-form-container">
          <form onSubmit={handleFormSubmit} className="compatibility-form">
            <h3>{editingEntry ? 'Edit Compatibility Rule' : 'Add New Compatibility Rule'}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Connector A (From) *</label>
                <input
                  type="text"
                  value={formData.connector_a}
                  onChange={(e) => setFormData({...formData, connector_a: e.target.value})}
                  placeholder="e.g., NC3MXX"
                  required
                />
              </div>

              <div className="form-group">
                <label>Connector B (To) *</label>
                <input
                  type="text"
                  value={formData.connector_b}
                  onChange={(e) => setFormData({...formData, connector_b: e.target.value})}
                  placeholder="e.g., NC3FXX"
                  required
                />
              </div>

              <div className="form-group">
                <label>Compatibility Status</label>
                <select
                  value={formData.compatible}
                  onChange={(e) => setFormData({...formData, compatible: e.target.value === 'true'})}
                >
                  <option value="true">✅ Compatible</option>
                  <option value="false">❌ Incompatible</option>
                </select>
              </div>

              <div className="form-group">
                <label>Connection Type</label>
                <select
                  value={formData.connection_type}
                  onChange={(e) => setFormData({...formData, connection_type: e.target.value})}
                >
                  <option value="balanced">Balanced</option>
                  <option value="unbalanced">Unbalanced</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div className="form-group">
                <label>Poles Used</label>
                <input
                  type="text"
                  value={formData.poles_used}
                  onChange={(e) => setFormData({...formData, poles_used: e.target.value})}
                  placeholder="e.g., 2, 3, 1+2"
                />
              </div>

              <div className="form-group full-width">
                <label>Warning Message</label>
                <input
                  type="text"
                  value={formData.warning}
                  onChange={(e) => setFormData({...formData, warning: e.target.value})}
                  placeholder="Optional warning for users"
                />
              </div>

              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this compatibility"
                  rows="2"
                />
              </div>

              {!formData.compatible && (
                <div className="form-group full-width">
                  <label>Incompatibility Reason</label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    placeholder="Why these connectors are incompatible"
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingEntry ? 'Update Rule' : 'Add Rule'}
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Compatibility Table */}
      <div className="compatibility-table-container">
        <table className="compatibility-table">
          <thead>
            <tr>
              <th>Connector A</th>
              <th>Connector B</th>
              <th>Status</th>
              <th>Type</th>
              <th>Poles</th>
              <th>Warning/Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentEntries.map((entry) => (
              <tr key={entry.id} className={entry.compatible ? 'compatible' : 'incompatible'}>
                <td className="connector-cell">
                  <strong>{entry.connector_a}</strong>
                </td>
                <td className="connector-cell">
                  <strong>{entry.connector_b}</strong>
                </td>
                <td className="status-cell">
                  {entry.compatible ? (
                    <span className="status-badge compatible">✅ Compatible</span>
                  ) : (
                    <span className="status-badge incompatible">❌ Incompatible</span>
                  )}
                </td>
                <td>{entry.connection_type || 'N/A'}</td>
                <td>{entry.poles_used || 'N/A'}</td>
                <td className="notes-cell">
                  {entry.warning && (
                    <div className="warning-text">⚠️ {entry.warning}</div>
                  )}
                  {entry.notes && (
                    <div className="notes-text">{entry.notes}</div>
                  )}
                  {entry.reason && (
                    <div className="reason-text">❌ {entry.reason}</div>
                  )}
                </td>
                <td className="actions-cell">
                  <button
                    onClick={() => handleEdit(entry)}
                    className="btn btn-small btn-secondary"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="btn btn-small btn-danger"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentEntries.length === 0 && (
          <div className="no-results">
            <p>No compatibility entries found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-secondary"
          >
            ← Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages} ({filteredData.length} entries)
          </span>
          
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-secondary"
          >
            Next →
          </button>
        </div>
      )}

      <style jsx>{`
        .compatibility-admin {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .admin-header {
          margin-bottom: 2rem;
          text-align: center;
        }

        .admin-header h2 {
          color: #333;
          margin-bottom: 0.5rem;
        }

        .admin-header p {
          color: #666;
          font-size: 1.1rem;
        }

        .loading-spinner {
          text-align: center;
          padding: 3rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 1rem;
          border-radius: 5px;
          margin-bottom: 1rem;
        }

        .save-status {
          padding: 0.75rem;
          border-radius: 5px;
          margin-bottom: 1rem;
          text-align: center;
          font-weight: 500;
        }

        .save-status.saving {
          background: #fff3cd;
          color: #856404;
        }

        .save-status.saved {
          background: #d4edda;
          color: #155724;
        }

        .save-status.error {
          background: #f8d7da;
          color: #721c24;
        }

        .admin-controls {
          background: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 2rem;
        }

        .control-group {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .search-input {
          flex: 1;
          min-width: 300px;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 1rem;
        }

        .filter-select {
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 1rem;
          background: white;
        }

        .stats {
          display: flex;
          gap: 2rem;
          font-size: 0.9rem;
          color: #666;
          flex-wrap: wrap;
        }

        .edit-form-container {
          background: white;
          border: 2px solid #007bff;
          border-radius: 8px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .compatibility-form h3 {
          margin-bottom: 1.5rem;
          color: #333;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #333;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 1rem;
        }

        .form-group textarea {
          resize: vertical;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
        }

        .compatibility-table-container {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin-bottom: 2rem;
        }

        .compatibility-table {
          width: 100%;
          border-collapse: collapse;
        }

        .compatibility-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }

        .compatibility-table td {
          padding: 1rem;
          border-bottom: 1px solid #dee2e6;
          vertical-align: top;
        }

        .compatibility-table tr:hover {
          background: #f8f9fa;
        }

        .compatibility-table tr.compatible {
          border-left: 4px solid #28a745;
        }

        .compatibility-table tr.incompatible {
          border-left: 4px solid #dc3545;
        }

        .connector-cell {
          font-family: 'Courier New', monospace;
          font-weight: 600;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 15px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-badge.compatible {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.incompatible {
          background: #f8d7da;
          color: #721c24;
        }

        .notes-cell {
          max-width: 300px;
        }

        .warning-text {
          color: #856404;
          background: #fff3cd;
          padding: 0.25rem 0.5rem;
          border-radius: 3px;
          margin-bottom: 0.25rem;
          font-size: 0.9rem;
        }

        .notes-text {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .reason-text {
          color: #721c24;
          background: #f8d7da;
          padding: 0.25rem 0.5rem;
          border-radius: 3px;
          font-size: 0.9rem;
        }

        .actions-cell {
          white-space: nowrap;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
          display: inline-block;
          transition: all 0.2s;
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .btn.btn-primary {
          background: #007bff;
          color: white;
        }

        .btn.btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn.btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn.btn-small {
          padding: 0.5rem;
          font-size: 0.9rem;
          margin-right: 0.5rem;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 5px;
        }

        .page-info {
          color: #666;
          font-weight: 500;
        }

        .no-results {
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        @media (max-width: 768px) {
          .compatibility-admin {
            padding: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .control-group {
            flex-direction: column;
            align-items: stretch;
          }

          .search-input {
            min-width: auto;
          }

          .stats {
            flex-direction: column;
            gap: 0.5rem;
          }

          .compatibility-table-container {
            overflow-x: auto;
          }

          .pagination {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CompatibilityAdmin;